/**
 * The wizard's five steps, as data.
 *
 * `.docs/20-studio-portal-pages.md` §B: "Each step is one action; the wizard is a
 * sequence, not a transaction." That is the whole design and this table is where it
 * is legible — one row per step, naming the action it runs, so a reader can check
 * "five steps, five verbs" without reading five components.
 *
 * **Every step commits on its own.** Leaving after step one leaves a client with no
 * retainer, which is a state the book renders (`.docs/20` §Edge cases) and not a
 * half-written record; leaving after step four leaves a client on a retainer whose
 * signup invoice the worker will issue with or without this browser. Nothing here
 * rolls anything back, and nothing needs to.
 */

/** Which screen is on. `?step=` in the URL, so a half-finished wizard is a link. */
export const STEP_KEYS = ['details', 'terms', 'owner', 'package', 'payment'] as const

export type StepKey = (typeof STEP_KEYS)[number]

export type Step = {
  readonly key: StepKey
  /** The word in the stepper. */
  readonly label: string
  /** The heading of the step itself, and what the reader is being asked to do. */
  readonly title: string
  /** The action this step runs, for the report's button → action table. */
  readonly action: string
  /** True for a step that has nothing to save — the reader may simply move on. */
  readonly skippable: boolean
}

export const STEPS: readonly Step[] = [
  {
    key: 'details',
    label: 'Account',
    title: 'Open the account',
    action: 'clients_create',
    skippable: false,
  },
  {
    key: 'terms',
    label: 'Terms',
    title: 'Tier and currency',
    action: 'clients_set_tier',
    skippable: true,
  },
  {
    key: 'owner',
    label: 'Seats',
    title: 'Who is on the account',
    action: 'clients_invite_member',
    skippable: true,
  },
  {
    key: 'package',
    label: 'Package',
    title: 'The retainer',
    action: 'retainers_start',
    skippable: false,
  },
  {
    key: 'payment',
    label: 'Paying',
    title: 'The first invoice, and how it gets paid',
    action: 'billing_run',
    skippable: true,
  },
]

/** Where a step sits in the sequence. `-1` for a key that is not one. */
export function indexOfStep(key: StepKey): number {
  return STEPS.findIndex((step) => step.key === key)
}

/** The step after this one, or null at the end. */
export function nextStep(key: StepKey): StepKey | null {
  return STEPS[indexOfStep(key) + 1]?.key ?? null
}

/** Every step but the first needs a client the first one created. */
export function needsClient(key: StepKey): boolean {
  return key !== 'details'
}
